// Project based config
// 	The only other differences between projects are in:
//		SetupLoader.jsx
//		MapLoader.jsx & dependant files

import {
	changeHeaderTransform,
	changeTransform,
	confidenceTransform,
	confidenceHeaderTransform,
	addLineHeightTransform,
	sortChange,
	sortConfidence,
} from "lib/table/tableTransforms.js";

import {
	baseContentColumns,
	contentColumnsGECOnly,
	contentColumnsAus22Only,
	dateTypeTitleSharesContentColumns,
	contentColumnsNoDuplicatesOrInteractions,
	// noDuplicatesContentColumns,
	noTypeContentColumns,
	// noTypeNoInteractionsContentColumns,
	noTypeNoDuplicatesContentColumns,
	noSharesContentColumns,
	vzlaContentColumns,
	platform2023ColumnsNoInteractions,
} from "lib/table/contentColumns.js";
import { contentTabBase, contentTabPushPullCountry, contentTabSource } from "lib/table/contentTabColumns.js";
import { us2020CrosstabsHeaders, gecCrosstabsHeaders, configureGECStats } from "lib/table/crosstabsColumns.js";
import { usFIPSLookup } from "lib/mapLookup.js";
import { predictSearchHeaders } from "lib/table/mediaColumns.js";
import { formatGECTags } from "lib/helpers/projectSpecificHelpers.js";
import { chinaUsStats, chinaUsStatsMongolia } from "lib/explodedDonutStatsConfig.js";

import { biasText, biasBullets, outletsText, outletsBullets, peopleText, peopleBullets } from "lib/helpers/crosstabsHelpers.js";
import { defaultChatConfig, defaultDocumentChatConfig, defaultMultiDocumentChatConfig} from "components/Chat/config.js";


const DS_PROCUREMENT = {
	name: "Procurement Data",
	id: "procurement",
	shortDescription:
		"Data on purchases by government & government-affiliated organziations including military, commercial, and academic procurements.",
	dataType: "Purchase records",
	sourceInfoPublic: "Government and institutional procurement websites and databases",
	showStats: true,
	connected: true,
	defaultEnabled: true,
	tableColumns: ["title", "sourceUrl", "relevancy"],
};

const DS_ENTITIES = {
	name: "Entities",
	id: "rover",
	shortDescription:
		"Knowledge graphs on companies, people, and other entities connected to U.S. adversary countries. Includes key entity attributes, affiliations, and dynamic content related to their activities. Many listed entities are subject to U.S. and international sanctions.",
	dataType: "Compiled knowledge base on entities",
	sourceInfoPublic: "Researched and compiled by Exovera analysts based on Exovera data and other sources",
	showStats: false,
	connected: true,
	defaultEnabled: true,
	tableColumns: ["label_en", "label", "entity_type", "categories", "primary_country", "sanctioned", "relevancy"],
};

const DS_CORP_REGISTRATIONS = 
{
	name: "Corporate Registration Data",
	id: "corprecords",
	shortDescription:
		"Corporate registration data from offiicial government sources and commercial providers. Includes information on companies' ownership, management, structure, financial status, and operational focus areas.",
	dataType: "Data records",
	sourceInfoPublic: "Various government corporate registration websites and commercial registration aggregators",
	showStats: false,
	connected: true,
	defaultEnabled: true,
	tableColumns: ["label", "business_scope", "industry", "enterprise_type", "relevancy"],
};

const DS_RUSSIA_CONTRACTS = {
	name: "Russia Contracts",
	id: "contract_awards",
	shortDescription: "Collection of Russian government purchases / procurements.",
	dataType: "Data records",
	sourceInfoPublic: "Exovera library from online sources and hard copy publications.",
	showStats: false,
	connected: true,
	defaultEnabled: true,
	tableColumns: [
		"contractNumber",
		"customerName",
		"customerINN",
		"supplierNames",
		"supplierINNs",
		"productNames",
		"contractDateConcluded",
		"relevancy",
	],
};

const DS_RESUMES = {
	name: "Resumes",
	id: "resumes",
	shortDescription:
		"Biographical dataset sourced from resumes, containing basic information on individuals' work and educational background.",
	dataType: "Scraped resumes",
	sourceInfoPublic: "Online and Exovera collections of Chinese resumes",
	showStats: false,
	connected: true,
	defaultEnabled: true,
	tableColumns: ["name", "gender", "current_position", "age", "relevancy"],
};

const DS_BOOKS = {
	name: "Books",
	id: "jbn",
	shortDescription: "Collection of adversary country books selected by Exovera.",
	dataType: "Full text files and PDF scans",
	sourceInfoPublic: "Exovera library from online sources and hard copy publications.",
	showStats: false,
	connected: false,
	defaultEnabled: false,
	tableColumns: ["title", "date", "relevancy"],
};

const DS_NEWSPAPERS = {
	name: "Newspapers",
	id: "newspaperpdfs",
	shortDescription: "Collection of adversary country newspapers selected by Exovera.",
	dataType: "PDF scans",
	sourceInfoPublic: "Exovera library from online sources and hard copy publications.",
	showStats: false,
	connected: false,
	defaultEnabled: false,
	tableColumns: ["source", "date", "relevancy"],
};

const DS_JOURNAL_ARTICLES = {
	name: "Journal Articles",
	id: "journalArticles",
	shortDescription: "Collection of adversary country academic and technical journal articles selected by Exovera.",
	dataType: "PDF scans",
	sourceInfoPublic: "Exovera library from online sources and hard copy publications.",
	showStats: false,
	connected: true,
	defaultEnabled: true,
	tableColumns: ["source", "title", "year", "relevancy"],
};

const DS_JOURNAL_METADATA = {
	name: "Journal Metadata",
	id: "journalmetadata",
	shortDescription:
		"An index of data on academic and technical research publications. Includes publication information, article name, author, publiciation date, topical focus, and summaries / abstracts when available.",
	dataType: "Data records",
	sourceInfoPublic: "Consolidated from online publication record databases",
	showStats: false,
	connected: true,
	defaultEnabled: true,
	tableColumns: ["id", "source", "title", "author", "year", "relevancy"],
};

const DS_CCP_MEMBERS_LEAK = {
	name: "CCP Members Leak",
	id: "profiledata_2016",
	shortDescription: "List of 1.9 million CCP members and associated member metadata from 2016.",
	dataType: "Data records",
	sourceInfoPublic: `List originally extracted by Chinese "dissidents" and leaked to multiple media outlets, including "The Australian"`,
	showStats: false,
	connected: true,
	defaultEnabled: true,
	tableColumns: ["name", "sex", "organization", "id_card_number", "hometown", "address", "relevancy"],
};

const DS_REFERENCES = {
	name: "References",
	id: "references",
	shortDescription: "Miscellaneous reference documents",
	dataType: "PDF scans",
	sourceInfoPublic: "Miscellaneous reference documents",
	showStats: false,
	connected: false,
	defaultEnabled: false,
	tableColumns: ["title", "year", "relevancy"],
};

const DS_AEROSPACE = {
	name: "Aerospace",
	id: "aerospace",
	shortDescription: "",
	dataType: "",
	sourceInfoPublic: "",
	showStats: false,
	connected: false,
	defaultEnabled: false,
	tableColumns: ["title", "date", "relevancy"],
};

const DS_GREENPARROT = {
	name: "GreenParrot",
	id: "greenparrot",
	shortDescription: "",
	dataType: "",
	sourceInfoPublic: "",
	showStats: false,
	connected: false,
	defaultEnabled: false,
	tableColumns: ["title", "date", "relevancy"],
};

const DS_IRAN_OIL_SANCTION_VIOLATIONS = {
	name: "Iran Oil/Sanctions Email Leak",
	id: "iran_oil_sanction_violations",
	shortDescription: "",
	dataType: "",
	sourceInfoPublic: "",
	showStats: false,
	connected: false,
	defaultEnabled: false,
	tableColumns: ["title", "date", "relevancy"],
};

const BASE_DATASETS =  [
	DS_PROCUREMENT,
	DS_ENTITIES,
	DS_CORP_REGISTRATIONS,
	DS_RUSSIA_CONTRACTS,
	DS_RESUMES,
	DS_BOOKS,
	DS_NEWSPAPERS,
	DS_JOURNAL_ARTICLES,
	DS_JOURNAL_METADATA,
	DS_CCP_MEMBERS_LEAK,
	DS_REFERENCES,
];

const WPC_CHINA = {
	name: "China",
	id: "dragon",	
};

const WPC_RUSSIA = {
	name: "Russia",
	id: "bear",	
};

// Atmospherics
const aatf = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: false, // need to set up api.dataKey
		reportBuilderLink: false,
		social: false,
		video: false, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["fa"],
	defaultZoom: "1 year",
	entityFiltersAvailable: false, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: false,
	contentTabDateBegin: 1643707910000,
	hideShare: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	mediaLanguages: ["English", "Pashto", "Dari"],
	userSearchHideLocation: true,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	mediaTranslateTitles: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

// Afghanistan
const afghanistandemo = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: true, // need to set up api.dataKey
		reportBuilderLink: true,
		social: true,
		video: true, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["fa"],
	defaultZoom: "1 year",
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: true,
	hideShare: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	mediaLanguages: ["English", "Pashto", "Dari"],
	userSearchHideLocation: true,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	mediaEntityFilters: true,
	mediaTranslateTitles: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	videoTabEndDate: "10-01-2021", // "MM-DD-YYYY"
	videoTabStartDate: "09-01-2021", // "MM-DD-YYYY"
	contentTabDateBegin: 1630468800000,
	contentTabDateEnd: 1633060800000,
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	translateArticleTitles: false,
	uiThemeToggle: true,
	videoTabHideTopics: true,
	videoTabEntities: true,
	videoTabEntityGraph: true,
	videoTabTopics: false,
	// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

// Afghanistan Demo
const afghanistanDFAT = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: true, // need to set up api.dataKey
		reportBuilderLink: true,
		social: false,
		video: true, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["fa"],
	defaultZoom: "1 year",
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: true,
	hideShare: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	mediaLanguages: ["English", "Pashto", "Dari"],
	userSearchHideLocation: true,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	mediaTranslateTitles: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	translateArticleTitles: false,
	uiThemeToggle: true,
	videoTabHideTopics: true,
	videoTabEntities: true,
	videoTabTopics: false,
	// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

// Baghdad
const africom = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: true, // need to set up api.dataKey
		reportBuilderLink: true,
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultZoom: "1 year",
	hideEntities: false,
	hideShare: false,
	defaultLightUI: false,
	userSearchHideLocation: true,
	mediaLanguages: ["English"],
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270 },
	uiThemeToggle: true,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["ku", "ar"],
	chatConfig: defaultChatConfig,
	// Google Analytics / Logrocket config
	gaMeasurementId: "G-LWMV2X2MEW",
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
	queryBuilderSupported: true,
};

// Fido
const arlis = {
	availableTabs: {
		default: "explorer",
		asksage: false,
		crosstabs: false,
		explorer: true,
		media: false, // need to set up api.dataKey
		narratives: false,
		social: false,
		video: false, // set api.videoFilters
		web: false,
		agents: false,
		bigMoversLink: false,
		duplicateToggle: false,
		curated: false,
		events: false,
		graph: false,
		moe: false,
		reportBuilderLink: false,
	},
	disableGeneralDownload: true,
	explorerDatasets: [
		{
			name: "Entities",
			id: "rover",
			showStats: false,
			tableColumns: [
				"label_en",
				"label",
				// "aka",
				"entity_type",
				"categories",
				"primary_country",
				"sanctioned",
				"relevancy",
				// "sanction_authority",
				// "sanction_program",
			],
		},
	],
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["zh"],
	googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	topPhrasesAvailable: false,
	translateArticleTitles: false,
	uiThemeToggle: true,
	// Google Analytics / Logrocket config
	gaMeasurementId: "G-8ZXKX8S8J6",
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

// Ask Sage
const asksage = {
	availableTabs: {
		default: "asksage",
		asksage: true,
	},
	entityFiltersAvailable: false,
	defaultLightUI: false,
	timezone: false,
	uiThemeToggle: true,
};

// Baseline Platform
const china = {
	// projectNotifications: [
	//   {
	//     notificationMessage: "Please be aware that scheduled maintenance is being performed to exoINSIGHT during the hours of 19:00 EST on Friday 25th October to 09:00 EST on Saturday 26th October. During this time access to data on the web pages tab of the application may be unavailable.",
	//     notificationType: "info",
	//     title: "System maintainance notification",
	//     uuid: "cbpLocal_1",
	//   },
	// ],
	availableTabs: {
		default: "curated",
		asksage: false,
		crosstabs: false,
		connector: true,
		explorer: true,
		media: true, // need to set up api.dataKey
		narratives: true,
		social: true,
		video: true, // set api.videoFilters
		web: true,
		agents: false,
		bigMoversLink: false,
		duplicateToggle: true,
		curated: true,
		events: false,
		graph: false,
		moe: false,
		reportBuilderLink: false,
	},
	alwaysShowContentInteractions: true,
	// Default supports nested narratives with clickable main narrative
	compareHeaderConfig: false,
	contentColumns: platform2023ColumnsNoInteractions,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabBase,
	contentTabMap: false,
	contentTabDateBegin: 1577836800000,
	// defaultAggregationLevel: "weeklyTimeseries", // defaults to "timeseries"
	desiredCrosstabsNarrativeOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
	// dmaMap: true,
	entityFiltersAvailable: true,
	crosstabsBargraphTopLeft: {
		id: "Organizations",
		name: "Organizations",
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		limit: 10, // optional
		// tooltipText: biasText, // optional
		// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		// disableMax: true, // FLAG_DISABLE_MAX - unsupported for now. will disable left side chartMax if any pass this (implementation sounds clunky - may want to rethink it as an additional config item)
	},
	crosstabsBargraphMiddleLeft: {
		id: "Companies",
		name: "Companies",
		limit: 10,
	},
	// crosstabsBargraphBottomLeft:
	crosstabsBargraphTopRight: {
		id: "Persons",
		name: "People",
		limit: 25,
		tooltipText: peopleText,
	},
	// crosstabsBargraphMiddleRight:
	// crosstabsBargraphBottomRight:
	// FLAG_DATA_HANDLING_FACET
	crosstabsNarrativeFacets: [
		{ name: "Political Bias", id: "default" },
		{ name: "State Leaning", id: "stateLeaning" },
	],
	crosstabsNarrativeHeaders: us2020CrosstabsHeaders,
	crosstabsNarrativeHeaderLabels: [
		{
			groupBy: "bias",
			label: "Political Bias",
			info: "Articles published by outlets, grouped by political bias",
		},
		{
			groupBy: "stateLeaning",
			label: "State Political Leaning",
			info: "Articles published in states, grouped by their projected political alignment in the 2020 election",
			bullets: [
				"Battleground (13): CO, FL, IA, ME, MI, MN, NC, NH, NV, OH, PA, VA, WI",
				"Left (19): CA, CT, DC, DE, HI, IL, MA, MD, NM, NJ, NY, OR, RI, VT, WA",
				"Right (21): AK, AL, AR, AZ, GA, ID, IN, KS, KY, LA, MO, OK, MS, MT, NE, ND, SC, SD, TN, TX, UT, WV, WY",
			],
		},
	],
	dataCatalogSupport: true,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["zh"],
	useInternalTranslation: false,
	// exoveraEditButton: true,
	expandedContentShowTweets: false,
	fakeSingleArticle: false,
	graphDataTypes: [
		{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Unique Article Count", id: "articleDedupCount", label: "Volume", perspectiveOverride: "Source" },
		// { name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
		// { name: "Article Reach", id: "articleReach", label: "Reach" },
	],
	hotArticleLimit: {
		hot: 1000,
		hotter: 5000,
		hottest: 10000,
	},
	hideEntities: false,
	hideMap: false,
	hideParentNarrativeTags: false,
	highlightedEntitiesTitle: "Candidates",
	defaultLightUI: false,
	projectMapConfig: false,
	// mapLookup: usFIPSLookup,
	mediaEntityFilters: true,
	userSearchHideLocation: true,
	mediaLanguages: ["English", "Chinese"],
	googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
	mediaTranslateTitles: false,
	moeGraphAvailable: false,
	explorerDatasets: [
		...BASE_DATASETS,
		DS_IRAN_OIL_SANCTION_VIOLATIONS,
	],
	// Temporary disabling of entities display due to NER not working in Chinese
	// explorerShowEntities: ["procurement"],
	moeMapAvailable: false,
	narrativeEntityTranslateToggle: false,
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	topPhrasesAvailable: false,
	translateArticleTitles: false,
	uiThemeToggle: true,
	// videoTabEndDate: "05-14-2020", // "MM-DD-YYYY"
	videoTabEntities: true,
	videoTabEntityGraph: true,
	videoTabStartDate: "01-01-2020", // "MM-DD-YYYY"
	videoTabTopics: false,
	chatConfig: defaultChatConfig,
	documentChatConfig: defaultDocumentChatConfig,
	multiDocumentChatConfig: defaultMultiDocumentChatConfig,
	useGlobalMaps: true,
	// Google Analytics / Logrocket config
	gaMeasurementId: "G-HMQNZJSPFP",
	logRocketAppId: "fsncm4/narrative-tracker",
	mapCountryIds: ["china"],
	queryBuilderSupported: true,
};

// Narrative Map Demo - Clone of Baseline Platform 
//  - Default narratives tab
//  - Global Maps enabled
//  - Interactions/Tweet displays enabled.
const narrativeMapDemo = {
	...china,
	availableTabs: {
		...china.availableTabs,
		default: "narratives",
		agents: false,
	},
	useGlobalMaps: true,
	expandedContentShowTweets: true,
	contentColumns: contentColumnsNoDuplicatesOrInteractions,
	mapCountryIds: ["china", "australia"],
	explorerDatasets: [
		...BASE_DATASETS,
		DS_AEROSPACE,
		DS_GREENPARROT,
	],
};


const AU_THUNDER_2025 = {
	...china,
	useInternalTranslation: true,
	googleTranslateLanguages: ["en"],
	explorerDatasets: BASE_DATASETS,
};


const CA_ORCA_2025 = {
	...china,
	gaMeasurementId: undefined,
	logRocketAppId: undefined,
	explorerDatasets: BASE_DATASETS,
};


const GLOVER = {
	...china,
	webPagesCollections: [
		WPC_CHINA,
	],
	explorerDatasets: [
		DS_PROCUREMENT,
		DS_ENTITIES,
		DS_CORP_REGISTRATIONS,
		DS_RESUMES,
		DS_BOOKS,
		{...DS_JOURNAL_ARTICLES, connected: false},
		{...DS_JOURNAL_METADATA, connected: false},
		DS_CCP_MEMBERS_LEAK,
		DS_REFERENCES,
	],
};

const VIOLIN = {
	...GLOVER,
};

const DIESEL = {
	...china,
	availableTabs: {
		default: "curated",
		connector: true,
		explorer: true,
		media: true, 
		narratives: true,
		web: true,
		curated: true,
	},
	webPagesCollections: [
		WPC_CHINA,
	],
	explorerDatasets: [
		DS_ENTITIES,
		DS_CORP_REGISTRATIONS,
		DS_NEWSPAPERS,
		DS_RESUMES,
		DS_BOOKS,
		DS_JOURNAL_ARTICLES,
		DS_CCP_MEMBERS_LEAK,
		DS_REFERENCES,
	],
};

const RELISH = {
	...china,
	availableTabs: {
		default: "curated",
		connector: true,
		explorer: true,
		media: true, 
		narratives: true,
		social: true,
		web: true,
		curated: true,
	},
	webPagesCollections: [
		WPC_CHINA,
	],
	explorerDatasets: [
		DS_PROCUREMENT,
		DS_ENTITIES,
		DS_CORP_REGISTRATIONS,
		DS_JOURNAL_ARTICLES,
		DS_JOURNAL_METADATA,
		DS_REFERENCES,
	],
};

// Scorpion - Clone of Baseline Platform - different/default reports tab.
const scorpion = {
	...china,
	availableTabs: {
		...china.availableTabs,
		default: "curated",
	},
	explorerDatasets: BASE_DATASETS,
	gaMeasurementId: "G-01N0EZ63HQ",
};

// Delphi - Clone of Baseline Platform - reports tab only, different reports.
const delphi = {
	...china,
	availableTabs: {
		curated: true,
		default: "curated",
	},
	explorerDatasets: BASE_DATASETS,
	contentTabDateBegin: 1262304000000,
	dataCatalogSupport: false,
};

// Thunder Trial - Clone of Baseline Platform - no user logging or dark web collection for web-pages.
const au2024_trial = {
	...china,
	gaMeasurementId: undefined,
	logRocketAppId: undefined,
	useInternalTranslation: true,
	googleTranslateLanguages: ["en"],
	explorerDatasets: BASE_DATASETS,
	webPagesCollections: [
		WPC_CHINA,
		WPC_RUSSIA,
	]
};

// Islamabad
const islamabad = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: true, // need to set up api.dataKey
		reportBuilderLink: true,
		social: false,
		video: true, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["ps", "ur"],
	defaultZoom: "1 month",
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: true,
	hideShare: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	mediaLanguages: ["English", "Urdu"],
	userSearchHideLocation: true,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	mediaTranslateTitles: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	translateArticleTitles: false,
	uiThemeToggle: true,
	videoTabHideTopics: true,
	videoTabEntities: true,
	videoTabTopics: false,
	// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
	queryBuilderSupported: true,
};

// Kabul
const kabul = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: true, // need to set up api.dataKey
		reportBuilderLink: true,
		social: false,
		video: true, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["fa", "ps"],
	defaultZoom: "1 year",
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: true,
	hideShare: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	mediaLanguages: ["English", "Pashto", "Dari"],
	userSearchHideLocation: true,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	mediaTranslateTitles: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	translateArticleTitles: false,
	uiThemeToggle: true,
	videoTabHideTopics: true,
	videoTabEntities: true,
	videoTabTopics: false,
	// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	// Google Analytics / Logrocket config
	gaMeasurementId: "G-RFSG141NQV",
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
	queryBuilderSupported: true,
};

// Koala
const koala = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		crosstabs: false,
		narratives: true,
		bigMoversLink: false,
		curated: true,
		events: false,
		explorer: true,
		graph: false,
		media: true,
		moe: false,
		reportBuilderLink: false,
		social: true,
		video: true,
		web: true,
		agents: false
	},
	alwaysShowContentInteractions: true,
	// Default supports nested narratives with clickable main narrative
	compareHeaderConfig: false,
	contentColumns: contentColumnsAus22Only,
	defaultAggregationLevel: "weeklyTimeseries",
	// dmaMap: true,a
	topPhrasesAvailable: false,
	hideMap: false,
	entityFiltersAvailable: false,
	contentTabDateBegin: 1646114400000,
	mediaEntityFilters: true,
	defaultOriginalLanguage: true,
	mediaLanguages: ["All", "English", "Chinese"],
	mediaCountries: ["All", "Australia", "China"],
	googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
	exoveraEditButton: true,
	expandedContentShowTweets: true,
	fakeSingleArticle: false,
	graphDataTypes: [
		{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Unique Article Count", id: "articleDedupCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
		// { name: "Article Reach", id: "articleReach", label: "Reach" },
	],
	hideEntities: false,
	hotArticleLimit: {
		hot: 1000,
		hotter: 5000,
		hottest: 10000,
	},
	configureStats: configureGECStats,
	defaultZoom: "1 year",
	hideParentNarrativeTags: false,
	highlightedEntitiesTitle: "Candidates",
	defaultLightUI: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["zh"],
	projectMapConfig: false,

	explorerDatasets: [
		{ name: "Procurement", id: "procurement", showStats: true, tableColumns: ["title", "province", "sourceUrl", "relevancy"] },
		{
			name: "Entities",
			id: "rover",
			showStats: false,
			tableColumns: [
				"label_en",
				"label",
				// "aka",
				"entity_type",
				"categories",
				"primary_country",
				"sanctioned",
				"relevancy",
				// "sanction_authority",
				// "sanction_program",
			],
		},
		{
			name: "Corporate Records",
			id: "corprecords",
			showStats: false,
			tableColumns: ["label", "business_scope", "industry", "enterprise_type", "relevancy"],
		},
		{ name: "Resumes", id: "resumes", showStats: false, tableColumns: ["name", "gender", "current_position", "age", "relevancy"] },
	],

	explorerShowEntities: ["procurement"],
	//   mediaLanguages: ["English", "Chinese"],
	// mapLookup: usFIPSLookup,
	moeGraphAvailable: false,
	moeMapAvailable: false,
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	// timezone: false,
	timezone: { name: " Australian Eastern Standard Time", offsetDisplay: "+10:00", offset: +0 },
	translateArticleTitles: false,
	uiThemeToggle: true,
	videoTabEntities: true,
	videoTabEntityGraph: true,
	videoTabStartDate: "01-01-2020", // "MM-DD-YYYY"
	videoTabTopics: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

// Russia
const russia = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		moe: true,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	aiToggle: false,
	hideMap: true,

	// aiToggleNoReaction: true,
	alwaysShowContentInteractions: true,
	// hideEntitiesInAIMode: true, // Linked to aiMode
	compareHeaderConfig: false,
	compareMultiSelect: true,
	// configureStats: configureGECStats, // for crosstabs
	contentColumns: contentColumnsGECOnly,
	// TODO: Install this and refactor summary-tab to consume it if the data ever support it
	// contentTabLabelPositions: {
	// 	topLeft: false,
	// 	topRight: "postDate",
	// 	bottomLeft: "featured",
	// 	bottomRight: false,
	// },
	// contentTabTableHeaders: contentTabBase,
	// defaultAggregationLevel: "weeklyTimeseries",
	defaultOriginalLanguage: true,
	defaultEntityType: "People",
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["ru"],
	// desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
	// emailSubscribeOption: "BAMO-alerts",
	entityFiltersAvailable: true, // <-----------
	expandedContentShowTweets: false,
	// crosstabsBargraphTopLeft: {
	// 	id: "Market",
	// 	name: "Target Market",
	// 	// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
	// 	limit: 15, // optional
	// 	// tooltipText: biasText, // optional
	// 	// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
	// },
	// crosstabsBargraphMiddleLeft: {
	// 	id: "Bias",
	// 	name: "Source Orientation",
	// 	limit: 15,
	// 	// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
	// 	tooltipText: biasText,
	// },
	// crosstabsBargraphMiddleBottomLeft: {
	// 	id: "Credibility",
	// 	name: "Credibility",
	// 	limit: 15,
	// },
	// crosstabsBargraphBottomLeft: {
	// 	id: "Language",
	// 	name: "Source Language",
	// 	limit: 15,
	// },
	// crosstabsBargraphTopRight: {
	// 	id: "Persons",
	// 	name: "People",
	// 	limit: 25,
	// 	tooltipText: peopleText,
	// },
	// FLAG_DATA_HANDLING_FACET
	// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
	// crosstabsNarrativeHeaders: gecCrosstabsHeaders,
	// crosstabsNarrativeHeaderLabels: [
	// 	{
	// 		groupBy: "lang",
	// 		label: "Language",
	// 		info: "Outlet language",
	// 	},
	// 	{
	// 		groupBy: "alignment",
	// 		label: "Kremlin Orientation",
	// 		info: "Outlet Kremlin alignment",
	// 	},
	// 	{
	// 		groupBy: "country",
	// 		label: "Country",
	// 		info: "Articles published within a given country",
	// 	},
	// ],
	duplicateToggle: false,
	fakeSingleArticle: false,
	graphDataTypes: [
		{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
		// { name: "Article Reach", id: "articleReach", label: "Reach" },
	],
	hideEntities: false,
	hideParentNarrativeTags: true,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false, // default to defaultLightUI?
	projectMapConfig: false,
	mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
	mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
	moeGraphAvailable: true,
	moeMapAvailable: false,
	moeTabName: "MOE",
	overrideColorsGently: true,
	projectSpecificTagFormat: formatGECTags,
	// mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
	// mediaPredictiveSelect: true,
	// mediaSearchHeaders: predictSearchHeaders,
	relevancySlider: false,
	showAllTopicsMap: false,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	statsPDF: true,
	timezone: false,
	translateArticleTitles: false,
	uiThemeToggle: true,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const oseRussia = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		crosstabs: true,
		media: true, // need to set up api.dataKey
		narratives: true,
		bigMoversLink: false,
		curated: false,
		events: false,
		explorer: true,
		graph: false,
		moe: false,
		reportBuilderLink: false,
		social: true,
		video: false,
		web: true,
		agents: false,
	},
	aiToggle: true,
	aiToggleNoReaction: true,
	alwaysShowContentInteractions: true,
	hideEntities: false,
	hideEntitiesInAIMode: true, // Linked to aiMode
	compareHeaderConfig: false,
	compareMultiSelect: true,
	configureStats: configureGECStats,
	contentColumns: contentColumnsGECOnly,
	// TODO: Install this and refactor summary-tab to consume it if the data ever support it
	// contentTabLabelPositions: {
	// 	topLeft: false,
	// 	topRight: "postDate",
	// 	bottomLeft: "featured",
	// 	bottomRight: false,
	// },
	contentTabTableHeaders: contentTabBase,
	defaultAggregationLevel: "weeklyTimeseries",
	defaultOriginalLanguage: true,
	defaultEntityType: "People",
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["ru"],
	desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
	emailSubscribeOption: "BAMO-alerts",
	entityFiltersAvailable: true, // <-----------
	expandedContentShowTweets: true,
	crosstabsBargraphTopLeft: {
		id: "Market",
		name: "Target Market",
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		limit: 15, // optional
		// tooltipText: biasText, // optional
		// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
	},
	crosstabsBargraphMiddleLeft: {
		id: "Bias",
		name: "Source Orientation",
		limit: 15,
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		tooltipText: biasText,
	},
	crosstabsBargraphMiddleBottomLeft: {
		id: "Credibility",
		name: "Credibility",
		limit: 15,
	},
	crosstabsBargraphBottomLeft: {
		id: "Language",
		name: "Source Language",
		limit: 15,
	},
	crosstabsBargraphTopRight: {
		id: "Persons",
		name: "People",
		limit: 25,
		tooltipText: peopleText,
	},
	// FLAG_DATA_HANDLING_FACET
	// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
	crosstabsNarrativeHeaders: gecCrosstabsHeaders,
	crosstabsNarrativeHeaderLabels: [
		{
			groupBy: "lang",
			label: "Language",
			info: "Outlet language",
		},
		{
			groupBy: "alignment",
			label: "Kremlin Orientation",
			info: "Outlet Kremlin alignment",
		},
		{
			groupBy: "country",
			label: "Country",
			info: "Articles published within a given country",
		},
	],
	duplicateToggle: false,
	fakeSingleArticle: false,
	graphDataTypes: [
		{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
		// { name: "Article Reach", id: "articleReach", label: "Reach" },
	],
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	socialPlatforms: [
		{ name: "Any", id: "all" },
		{ name: "Telegram", id: "telegram" },
		{ name: "X", id: "twitter" },
	],
	socialContains: [
		{ name: "Any", id: "all" },
		{ name: "Images", id: "smToolContainsImages" },
		{ name: "Videos", id: "smToolContainsVideos" },
	],
	explorerDatasets: [
		{
			name: "Entities",
			id: "rover",
			showStats: false,
			tableColumns: [
				"label_en",
				"label",
				// "aka",
				"entity_type",
				"categories",
				"primary_country",
				"sanctioned",
				// "sanction_authority",
				// "sanction_program",
				"relevancy",
			],
		},
		{
			name: "Russia Contracts",
			id: "contract_awards",
			showStats: false,
			tableColumns: [
				"contractNumber",
				"customerName",
				"customerINN",
				"supplierNames",
				"supplierINNs",
				"productNames",
				"contractDateConcluded",
				"relevancy",
			],
		},
		{
			name: "Publications",
			showStats: false,
			id: "jbn",
			tableColumns: ["type", "source", "title", "date", "relevancy"],
		},
	],
	defaultLightUI: false, // default to defaultLightUI?
	projectMapConfig: false,
	mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
	mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColorsGently: true,
	projectSpecificTagFormat: formatGECTags,
	hideParentNarrativeTags: true,
	mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
	// mediaPredictiveSelect: true,
	// mediaSearchHeaders: predictSearchHeaders,
	relevancySlider: false,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	statsPDF: true,
	timezone: false,
	translateArticleTitles: false,
	uiThemeToggle: true,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

//
// Candidtates for removal
//
const brasilia = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: false, // need to set up api.dataKey
		reportBuilderLink: true,
		social: false,
		video: true, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	defaultZoom: "1 year",
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: true,
	hideShare: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	mediaLanguages: ["English", "Pashto", "Dari"],
	userSearchHideLocation: true,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	mediaTranslateTitles: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	translateArticleTitles: false,
	uiThemeToggle: true,
	videoTabHideTopics: true,
	videoTabEntities: true,
	videoTabTopics: false,
	videoTabEndDate: "10-21-2021", // "MM-DD-YYYY"
	videoTabStartDate: "10-06-2021", // "MM-DD-YYYY"
	contentTabDateBegin: 1630468800000,
	contentTabDateEnd: 1634875200000,
	// videoTabStartDate: "01-01-2021", // "MM-DD-YYYY"
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const dcos = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: true, // need to set up api.dataKey
		reportBuilderLink: true,
		social: true,
		video: true, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["ur"],
	defaultZoom: "1 year",
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: true,
	hideShare: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	userSearchHideLocation: true,
	mediaLanguages: ["All", "English", "Pashto", "Dari"],
	googleTranslateLanguages: ["en", "ps", "fa"],
	mediaTranslateTitles: false,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	translateArticleTitles: false,
	uiThemeToggle: true,
	videoTabEntities: true,
	videoTabStartDate: "12-01-2021", // "MM-DD-YYYY"
	videoTabTopics: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const texas23 = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		moe: true,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	// Default supports nested narratives with clickable main narrative
	compareHeaderConfig: [
		{ id: "narratives", header: "Narratives" },
		{ id: "additionalSearches", header: "Other Trends" },
	],
	entityFiltersAvailable: true,
	fakeSingleArticle: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	hideEntities: false,
	hideParentNarrativeTags: false,
	defaultLightUI: false,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	projectMapConfig: {
		narratives: {
			state: { name: "Texas 23rd", geoState: "texas23" },
		},
		compare: {
			state: { name: "Texas 23rd", geoState: "texas23" },
		},
		moe: {
			state: { name: "Texas 23rd", geoState: "texas23" },
		},
	},
	moeGraphAvailable: true,
	moeMapAvailable: true,
	overrideColors: true,
	showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

// Cloned from GEC - gec
const balticsDemo = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		crosstabs: true,
		media: true, // need to set up api.dataKey
		narratives: true,
		bigMoversLink: false,
		curated: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	aiToggle: true,
	aiToggleNoReaction: true,
	alwaysShowContentInteractions: true,
	hideEntities: false,
	hideEntitiesInAIMode: true, // Linked to aiMode
	compareHeaderConfig: false,
	compareMultiSelect: true,
	configureStats: configureGECStats,
	contentColumns: contentColumnsGECOnly,
	// TODO: Install this and refactor summary-tab to consume it if the data ever support it
	// contentTabLabelPositions: {
	// 	topLeft: false,
	// 	topRight: "postDate",
	// 	bottomLeft: "featured",
	// 	bottomRight: false,
	// },
	contentTabTableHeaders: contentTabBase,
	defaultAggregationLevel: "weeklyTimeseries",
	defaultOriginalLanguage: true,
	defaultEntityType: "People",
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["ru"],
	desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
	emailSubscribeOption: "BAMO-alerts",
	entityFiltersAvailable: true, // <-----------
	crosstabsBargraphTopLeft: {
		id: "Market",
		name: "Target Market",
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		limit: 15, // optional
		// tooltipText: biasText, // optional
		// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
	},
	crosstabsBargraphMiddleLeft: {
		id: "Bias",
		name: "Source Orientation",
		limit: 15,
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		tooltipText: biasText,
	},
	crosstabsBargraphMiddleBottomLeft: {
		id: "Credibility",
		name: "Credibility",
		limit: 15,
	},
	crosstabsBargraphBottomLeft: {
		id: "Language",
		name: "Source Language",
		limit: 15,
	},
	crosstabsBargraphTopRight: {
		id: "Persons",
		name: "People",
		limit: 25,
		tooltipText: peopleText,
	},
	// FLAG_DATA_HANDLING_FACET
	// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
	crosstabsNarrativeHeaders: gecCrosstabsHeaders,
	crosstabsNarrativeHeaderLabels: [
		{
			groupBy: "lang",
			label: "Language",
			info: "Outlet language",
		},
		{
			groupBy: "alignment",
			label: "Kremlin Orientation",
			info: "Outlet Kremlin alignment",
		},
		{
			groupBy: "country",
			label: "Country",
			info: "Articles published within a given country",
		},
	],
	duplicateToggle: false,
	fakeSingleArticle: false,
	graphDataTypes: [
		{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
		// { name: "Article Reach", id: "articleReach", label: "Reach" },
	],
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false, // default to defaultLightUI?
	projectMapConfig: false,
	mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
	mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColorsGently: true,
	projectSpecificTagFormat: formatGECTags,
	hideParentNarrativeTags: true,
	mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
	// mediaPredictiveSelect: true,
	// mediaSearchHeaders: predictSearchHeaders,
	relevancySlider: false,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	statsPDF: true,
	timezone: false,
	translateArticleTitles: false,
	uiThemeToggle: true,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const russianFocus = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		crosstabs: true,
		media: true, // need to set up api.dataKey
		narratives: true,
		bigMoversLink: false,
		curated: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	aiToggle: true,
	aiToggleNoReaction: true,
	alwaysShowContentInteractions: true,
	hideEntities: false,
	hideEntitiesInAIMode: true, // Linked to aiMode
	compareHeaderConfig: false,
	compareMultiSelect: true,
	configureStats: configureGECStats,
	contentColumns: contentColumnsGECOnly,
	// TODO: Install this and refactor summary-tab to consume it if the data ever support it
	// contentTabLabelPositions: {
	//      topLeft: false,
	//      topRight: "postDate",
	//      bottomLeft: "featured",
	//      bottomRight: false,
	// },
	contentTabTableHeaders: contentTabBase,
	defaultAggregationLevel: "weeklyTimeseries",
	defaultOriginalLanguage: true,
	defaultEntityType: "People",
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["ru"],
	desiredCrosstabsNarrativeOrder: ["Russian", "Non-Russian", "Kremlin-independent", "Kremlin-aligned", "Grey/Unknown"],
	emailSubscribeOption: "BAMO-alerts",
	entityFiltersAvailable: true, // <-----------
	crosstabsBargraphTopLeft: {
		id: "Market",
		name: "Target Market",
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		limit: 15, // optional
		// tooltipText: biasText, // optional
		// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
	},
	crosstabsBargraphMiddleLeft: {
		id: "Bias",
		name: "Source Orientation",
		limit: 15,
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		tooltipText: biasText,
	},
	crosstabsBargraphMiddleBottomLeft: {
		id: "Credibility",
		name: "Credibility",
		limit: 15,
	},
	crosstabsBargraphBottomLeft: {
		id: "Language",
		name: "Source Language",
		limit: 15,
	},
	crosstabsBargraphTopRight: {
		id: "Persons",
		name: "People",
		limit: 25,
		tooltipText: peopleText,
	},
	// FLAG_DATA_HANDLING_FACET
	// crosstabsNarrativeFacets: [{ name: "Outlet Alignment", id: "default" }, { name: "Country", id: "country" }],
	crosstabsNarrativeHeaders: gecCrosstabsHeaders,
	crosstabsNarrativeHeaderLabels: [
		{
			groupBy: "lang",
			label: "Language",
			info: "Outlet language",
		},
		{
			groupBy: "alignment",
			label: "Kremlin Orientation",
			info: "Outlet Kremlin alignment",
		},
		{
			groupBy: "country",
			label: "Country",
			info: "Articles published within a given country",
		},
	],
	duplicateToggle: false,
	fakeSingleArticle: false,
	graphDataTypes: [
		{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
		// { name: "Article Reach", id: "articleReach", label: "Reach" },
	],
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false, // default to defaultLightUI?
	projectMapConfig: false,
	mapSourceTooltipText: "The deviation from the average number of published articles in a country that support a given narrative.",
	mapAudienceTooltipText: "The deviation from the average reaction to articles in a country that support a given narrative.",
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColorsGently: true,
	projectSpecificTagFormat: formatGECTags,
	hideParentNarrativeTags: true,
	mediaLanguages: ["Latvian", "Russian", "Estonian", "Lithuanian", "Romanian"],
	// mediaPredictiveSelect: true,
	// mediaSearchHeaders: predictSearchHeaders,
	relevancySlider: false,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	statsPDF: true,
	timezone: false,
	translateArticleTitles: false,
	uiThemeToggle: true,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const bcbs = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentColumns: noTypeContentColumns,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	disableFilters: ["audienceGender", "audiencePolitics", "audienceType"],
	disableFiltersMessage:
		"Models for individual account types, including gender, political leaning, and account type are currently being processed, please check back later.",
	duplicateToggle: true,
	entityFiltersAvailable: false,
	expandedContentShowTweets: true,
	fakeSingleArticle: false,
	hideEntities: false,
	hideMapIndicatorInGraph: true,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: true,
	projectMapConfig: false,
	moeGraphAvailable: false, // <-----------
	moeMapAvailable: true,
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const dccc = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentColumns: noTypeContentColumns,
	// disableFilters: ["audienceGender", "audiencePolitics", "audienceType"],
	// disableFiltersMessage:
	// 	"Models for individual account types, including gender, political leaning, and account type are currently being processed, please check back later.",
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	duplicateToggle: true,
	entityFiltersAvailable: false,
	expandedContentShowTweets: true,
	fakeSingleArticle: false,
	hideEntities: false,
	hideMapIndicatorInGraph: true,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: true,
	projectMapConfig: false,
	moeGraphAvailable: false, // <-----------
	moeMapAvailable: true,
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const midterms = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		moe: true,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	contentColumns: noTypeContentColumns,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	duplicateToggle: true,
	entityFiltersAvailable: false,
	fakeSingleArticle: false,
	hideEntities: false,
	hideMapIndicatorInGraph: true,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: true,
	projectMapConfig: false,
	moeGraphAvailable: false, // <-----------
	moeMapAvailable: true,
	noReaction: true,
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const iraq = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	entityFiltersAvailable: false, // <-----------
	fakeSingleArticle: true, // <----------- using USCC endpoint too
	hideEntities: false,
	hotArticleLimit: {
		hot: 1000,
		hotter: 2000,
		hottest: 5000,
	},
	defaultLightUI: true,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["fa"],
	projectMapConfig: false,
	moeGraphAvailable: false, // <-----------
	moeMapAvailable: true,
	overrideColors: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const cbp = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		graph: true,
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		media: false,
		moe: false,
		narratives: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	// additionalLegendHeaders: [
	// 	{
	// 		name: "Predictive",
	// 		id: "predictiveVal",
	// 		hideOnSmall: true,
	// 		width: 85,
	// 		minWidth: 800,
	// 	},
	// 	{
	// 		name: "Optimal Lag",
	// 		id: "optimalLag",
	// 		hideOnSmall: true,
	// 		width: 80,
	// 		minWidth: 800,
	// 		transform: lagTransform,
	// 	},
	// ],
	compareHeaderConfig: false,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Date",
			id: "postDate",
		},
		{
			name: "Analysis",
			id: "analysis",
		},
		{
			name: "Analysis Date",
			id: "analysisDate",
		},
		{
			name: "Impact Horizon",
			id: "impactHorizon",
		},
		{
			name: "Push Pull",
			id: "pushPull",
		},
		{
			name: "Signal Type",
			id: "signalType",
		},
	],
	contentTabTableHeaders: contentTabPushPullCountry,
	contentTabMap: false,
	contentTabDateBegin: 1559390400000,
	contentTabDateEnd: 1569844800000,
	defaultZoom: "1 year",
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [""],
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	// graphTabFacets: [
	// 	{
	// 		name: "Gender",
	// 		id: "gender",
	// 		width: 83,
	// 		options: [
	// 			{
	// 				name: "All",
	// 				id: "gender_all",
	// 				parent: "gender",
	// 			},
	// 			{
	// 				name: "Male",
	// 				id: "gender_male",
	// 				parent: "gender",
	// 			},
	// 			{
	// 				name: "Female",
	// 				id: "gender_female",
	// 				parent: "gender",
	// 			},
	// 		],
	// 	},
	// 	{
	// 		name: "UAC",
	// 		id: "uac",
	// 		info: "Unaccompanied Alien Children",
	// 		width: 60,
	// 		options: [
	// 			{
	// 				name: "All",
	// 				id: "uac_all",
	// 				parent: "uac",
	// 			},
	// 			{
	// 				name: "Yes",
	// 				id: "uac_yes",
	// 				parent: "uac",
	// 			},
	// 			{
	// 				name: "No",
	// 				id: "uac_no",
	// 				parent: "uac",
	// 			},
	// 		],
	// 	},
	// 	{
	// 		name: "Citizenship",
	// 		id: "citizenship",
	// 		width: 108,
	// 		options: [
	// 			{
	// 				name: "All",
	// 				id: "citizenship_all",
	// 				parent: "citizenship",
	// 			},
	// 			{
	// 				name: "Guatemala",
	// 				id: "citizenship_guatemala",
	// 				parent: "citizenship",
	// 			},
	// 			{
	// 				name: "Honduras",
	// 				id: "citizenship_honduras",
	// 				parent: "citizenship",
	// 			},
	// 			{
	// 				name: "El Salvador",
	// 				id: "citizenship_el_salvador",
	// 				parent: "citizenship",
	// 			},
	// 		],
	// 	},
	// ],
	graphTabSelectorColumns: [
		{
			name: "Search",
			id: "name",
			width: 3,
			fixed: false,
			transform: addLineHeightTransform,
		},
		{
			name: "Location",
			id: "locationStr",
			width: 100,
			fixed: true,
		},
		{
			name: "Immediate",
			id: "immediate",
			type: "prediction", // Used for local filtering
			width: 115,
			fixed: true,
			paddingLeft: 20,
			reverseSort: true,
			headerTransform: changeHeaderTransform,
			transform: changeTransform,
			sortColumn: sortChange,
		},
		{
			name: "Immediate Confidence",
			id: "immediate",
			type: "prediction", // Used for local filtering
			width: 60,
			paddingLeft: 10,
			fixed: true,
			reverseSort: true,
			headerTransform: confidenceHeaderTransform,
			transform: confidenceTransform,
			sortColumn: sortConfidence,
		},
		{
			name: "Short",
			id: "short",
			type: "prediction", // Used for local filtering
			width: 80,
			fixed: true,
			paddingLeft: 20,
			reverseSort: true,
			headerTransform: changeHeaderTransform,
			transform: changeTransform,
			sortColumn: sortChange,
		},
		{
			name: "Short Confidence",
			id: "short",
			type: "prediction", // Used for local filtering
			width: 60,
			paddingLeft: 10,
			fixed: true,
			reverseSort: true,
			headerTransform: confidenceHeaderTransform,
			transform: confidenceTransform,
			sortColumn: sortConfidence,
		},
		{
			name: "Medium",
			id: "mid",
			type: "prediction", // Used for local filtering
			width: 95,
			fixed: true,
			paddingLeft: 20,
			reverseSort: true,
			headerTransform: changeHeaderTransform,
			transform: changeTransform,
			sortColumn: sortChange,
		},
		{
			name: "Medium Confidence",
			id: "mid",
			type: "prediction", // Used for local filtering
			width: 60,
			paddingLeft: 10,
			fixed: true,
			reverseSort: true,
			headerTransform: confidenceHeaderTransform,
			transform: confidenceTransform,
			sortColumn: sortConfidence,
		},
		{
			name: "Long",
			id: "long",
			type: "prediction", // Used for local filtering
			width: 75,
			fixed: true,
			paddingLeft: 20,
			reverseSort: true,
			headerTransform: changeHeaderTransform,
			transform: changeTransform,
			sortColumn: sortChange,
		},
		{
			name: "Long Confidence",
			id: "long",
			type: "prediction", // Used for local filtering
			width: 60,
			paddingLeft: 10,
			fixed: true,
			reverseSort: true,
			headerTransform: confidenceHeaderTransform,
			transform: confidenceTransform,
			sortColumn: sortConfidence,
		},
	],
	hideEntities: false,
	hideParentNarrativeTags: true,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	projectMapConfig: false,
	// methodologyLink: "src/static/CBP_Methodology.docx",
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	percentageTypes: ["predata"],
	// projectNotifications: [
	// 	{
	// 		// notificationDate
	// 		notificationMessage:
	// 			"The data is currently updating. There may be some inconsistencies until it is complete.",
	// 		notificationType: "maintenance",
	// 		title: "Data update underway",
	// 		uuid: "cbpLocal_1",
	// 	},
	// ],
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const pnnl = {
	availableTabs: {
		default: "video",
		asksage: false,
		crosstabs: false,
		explorer: true,
		curated: false,
		media: true, // need to set up api.dataKey
		narratives: false,
		social: true,
		video: true, // set api.videoFilters
		web: true,
		agents: false,
		bigMoversLink: false,
		events: false,
		graph: false,
		moe: false,
		reportBuilderLink: false,
	},
	alwaysShowContentInteractions: false,
	// Default supports nested narratives with clickable main narrative
	compareHeaderConfig: false,
	contentColumns: baseContentColumns,
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabBase,
	contentTabMap: false,
	defaultSearchTranslationEnabled: true,
	defaultSearchTranslationLanguages: ["zh"],
	// defaultAggregationLevel: "weeklyTimeseries", // defaults to "timeseries"
	desiredCrosstabsNarrativeOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
	// dmaMap: true,
	entityFiltersAvailable: true,
	crosstabsBargraphTopLeft: {
		id: "Organizations",
		name: "Organizations",
		// desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"], // optional
		limit: 10, // optional
		// tooltipText: biasText, // optional
		// tooltipBullets: ["string", "string"], // optional - supported, just not in use currently
		// disableMax: true, // FLAG_DISABLE_MAX - unsupported for now. will disable left side chartMax if any pass this (implementation sounds clunky - may want to rethink it as an additional config item)
	},
	crosstabsBargraphMiddleLeft: {
		id: "Companies",
		name: "Companies",
		limit: 10,
	},
	// crosstabsBargraphBottomLeft:
	crosstabsBargraphTopRight: {
		id: "Persons",
		name: "People",
		limit: 25,
		tooltipText: peopleText,
	},
	// crosstabsBargraphMiddleRight:
	// crosstabsBargraphBottomRight:
	// FLAG_DATA_HANDLING_FACET
	crosstabsNarrativeFacets: [
		{ name: "Political Bias", id: "default" },
		{ name: "State Leaning", id: "stateLeaning" },
	],
	crosstabsNarrativeHeaders: us2020CrosstabsHeaders,
	crosstabsNarrativeHeaderLabels: [
		{
			groupBy: "bias",
			label: "Political Bias",
			info: "Articles published by outlets, grouped by political bias",
		},
		{
			groupBy: "stateLeaning",
			label: "State Political Leaning",
			info: "Articles published in states, grouped by their projected political alignment in the 2020 election",
			bullets: [
				"Battleground (13): CO, FL, IA, ME, MI, MN, NC, NH, NV, OH, PA, VA, WI",
				"Left (19): CA, CT, DC, DE, HI, IL, MA, MD, NM, NJ, NY, OR, RI, VT, WA",
				"Right (21): AK, AL, AR, AZ, GA, ID, IN, KS, KY, LA, MO, OK, MS, MT, NE, ND, SC, SD, TN, TX, UT, WV, WY",
			],
		},
	],
	// exoveraEditButton: true,
	expandedContentShowTweets: false,
	fakeSingleArticle: false,
	graphDataTypes: [
		{ name: "Article Count", id: "articleCount", label: "Volume", perspectiveOverride: "Source" },
		{ name: "Article Interactions", id: "articleInteractions", label: "Interactions", perspectiveOverride: "Audience" },
		// { name: "Article Reach", id: "articleReach", label: "Reach" },
	],
	hotArticleLimit: {
		hot: 1000,
		hotter: 5000,
		hottest: 10000,
	},
	hideEntities: false,
	hideMap: true,
	hideParentNarrativeTags: false,
	highlightedEntitiesTitle: "Candidates",
	defaultLightUI: false,
	projectMapConfig: false,
	// mapLookup: usFIPSLookup,
	mediaEntityFilters: false,
	userSearchHideLocation: true,
	explorerDatasets: [
		{ name: "Procurement", id: "procurement", showStats: true, tableColumns: ["title", "province", "sourceUrl"] },
		{
			name: "Entities",
			id: "rover",
			showStats: false,
			tableColumns: [
				"label_en",
				"label",
				// "aka",
				"entity_type",
				"categories",
				"primary_country",
				"sanctioned",
				// "sanction_authority",
				// "sanction_program",
			],
		},
		{
			name: "Corporate Records",
			id: "corprecords",
			showStats: false,
			tableColumns: ["label", "business_scope", "industry", "enterprise_type"],
		},
		{ name: "Resumes", id: "resumes", showStats: false, tableColumns: ["name", "gender", "current_position", "age"] },
	],
	explorerShowEntities: ["procurement"],
	mediaLanguages: ["English", "Chinese"],
	googleTranslateLanguages: ["en", "zh-CN", "zh-TW"],
	mediaTranslateTitles: false,
	moeGraphAvailable: false,
	moeMapAvailable: false,
	narrativeEntityTranslateToggle: false,
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	topPhrasesAvailable: false,
	translateArticleTitles: false,
	uiThemeToggle: true,
	// videoTabEndDate: "05-14-2020", // "MM-DD-YYYY"
	videoTabEntities: true,
	videoTabEntityGraph: true,
	videoTabStartDate: "01-01-2020", // "MM-DD-YYYY"
	videoTabTopics: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const bsca = {
	availableTabs: {
		default: "curated",
		asksage: false,
		curated: true,
		media: true, // need to set up api.dataKey
		reportBuilderLink: true,
		video: true, // set api.videoFilters
		bigMoversLink: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		moe: false,
		narratives: false,
		social: false,
		web: false,
		agents: false,
	},
	contentTabCsvHeaders: [
		{
			name: "Title",
			id: "title",
		},
		{
			name: "Source",
			id: "source",
		},
		{
			name: "Date",
			id: "postDate",
		},
	],
	contentTabTableHeaders: contentTabSource,
	contentTabMap: false,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	defaultZoom: "1 year",
	expandedContentShowTweets: false,
	hideEntities: false,
	hideShare: false,
	defaultLightUI: false,
	userSearchHideLocation: true,
	mediaLanguages: ["English"],
	// timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	uiThemeToggle: true,
	videoTabEntities: true,
	videoTabTopics: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const sevenTwenty = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: [
		{ id: "narratives", header: "Narratives" },
		{ id: "additionalSearches", header: "Other Trends" },
	],
	contentColumns: noTypeContentColumns,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	duplicateToggle: true,
	entityFiltersAvailable: true,
	expandedContentShowTweets: true,
	fakeSingleArticle: false,
	hideEntities: false,
	hideMapIndicatorInGraph: true,
	hotArticleLimit: {
		hot: 1000,
		hotter: 2000,
		hottest: 5000,
	},
	defaultLightUI: true,
	projectMapConfig: false,
	moeGraphAvailable: false, // <-----------
	moeMapAvailable: true,
	overrideColors: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const sevenTwenty_uscc = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	// Default supports nested narratives with clickable main narrative
	compareHeaderConfig: [
		{ id: "narratives", header: "Narratives" },
		{ id: "additionalSearches", header: "Other Trends" },
	],
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	entityFiltersAvailable: true,
	fakeSingleArticle: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	hideEntities: false,
	hideParentNarrativeTags: false,
	defaultLightUI: true,
	projectMapConfig: {
		narratives: {
			state: { name: "Texas 23rd", geoState: "texas23" },
		},
		compare: {
			state: { name: "Texas 23rd", geoState: "texas23" },
		},
		moe: {
			state: { name: "Texas 23rd", geoState: "texas23" },
		},
	},
	moeGraphAvailable: true,
	moeMapAvailable: true,
	overrideColors: true,
	showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const usip = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: false,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	entityFiltersAvailable: true, // <-----------
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: true,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	projectMapConfig: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: { name: "AFT", offsetDisplay: "+4:30", offset: +270, displayInSourceTimeZone: true },
	uiThemeToggle: true,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const venezuela = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: [{ id: "narratives", header: "Narratives" }],
	contentColumns: vzlaContentColumns,
	contentHeaderSortAudienceDefault: "fdate", // non-config default is "shares"
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	entityFiltersAvailable: false, // <-----------
	expandedContentShowTweets: true,
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	projectMapConfig: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

// venezuela clone
const sayara = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		crosstabs: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	compareHeaderConfig: [{ id: "narratives", header: "Narratives" }],
	contentColumns: vzlaContentColumns,
	contentHeaderSortAudienceDefault: "fdate", // non-config default is "shares"
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	entityFiltersAvailable: false, // <-----------
	expandedContentShowTweets: true,
	fakeSingleArticle: false,
	hideEntities: false,
	hideParentNarrativeTags: false,
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	defaultLightUI: false,
	projectMapConfig: false,
	moeGraphAvailable: true,
	moeMapAvailable: true,
	moeTabName: "MOE",
	overrideColors: true,
	showTopicsInGraph: false, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

const walmart = {
	availableTabs: {
		default: "narratives",
		asksage: false,
		crosstabs: true,
		narratives: true,
		bigMoversLink: false,
		curated: false,
		events: false,
		explorer: false,
		graph: false,
		media: false,
		moe: false,
		reportBuilderLink: false,
		social: false,
		video: false,
		web: false,
		agents: false,
	},
	altProjectForCrosstabsNarratives: "politics2020c4c66e01afca57f4896cd084f0c73df0",
	// Default supports nested narratives with clickable main narrative
	compareHeaderConfig: false,
	contentColumns: dateTypeTitleSharesContentColumns,
	defaultSearchTranslationEnabled: false,
	defaultSearchTranslationLanguages: [],
	entityFiltersAvailable: true,
	crosstabsBargraphTopLeft: {
		id: "Bias",
		name: "Political Bias",
		desiredOrder: ["Left", "Left-Center", "Least", "Right-Center", "Right"],
		tooltipText: biasText,
	},
	crosstabsBargraphMiddleLeft: {
		id: "Outlets",
		name: "Outlets",
		limit: 18,
	},
	crosstabsBargraphTopRight: {
		id: "Persons",
		name: "People",
		limit: 25,
		tooltipText: peopleText,
	},
	// expandedContentShowTweets: true,
	fakeSingleArticle: false,
	forceCrosstabsEntity: "Walmart",
	hotArticleLimit: {
		hot: 50,
		hotter: 100,
		hottest: 500,
	},
	hideEntities: false,
	hideParentNarrativeTags: false,
	defaultLightUI: false,
	projectMapConfig: false,
	moeGraphAvailable: false,
	moeMapAvailable: false,
	overrideColors: true,
	showAllTopicsMap: true,
	showTopicsInGraph: true, // Includes topic lines in the graph. false shows topics only in isolation
	timezone: false,
	uiThemeToggle: false,
	// Google Analytics / Logrocket config
	gaMeasurementId: undefined,
	logRocketAppId: "fsncm4/narrative-tracker",
	useInternalTranslation: false,
};

//
// Active Projects
//
const projectConfig = {
	aatf,
	afghanistandemo,
	afghanistanDFAT,
	africom,
	arlis,
	asksage,
	au2024_trial,
	china,
	delphi,
	scorpion,
	narrativeMapDemo,
	AU_THUNDER_2025,
	CA_ORCA_2025,
	GLOVER,
	VIOLIN,
	DIESEL,
	RELISH,
	islamabad,
	kabul,
	koala,
	russia,
	oseRussia,
	brasilia,
	dcos,
	texas23,
	balticsDemo,
	russianFocus,
	bcbs,
	dccc,
	midterms,
	iraq,
	cbp,
	pnnl,
	bsca,
	sevenTwenty,
	sevenTwenty_uscc,
	usip,
	venezuela,
	sayara,
	walmart,
};

export default projectConfig;